import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, UserPlus, Users, Blocks, Pencil, TrendingUp, Download, LogIn } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PlatformAccountDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { enterAccount } = useAuth();
  const [data, setData] = useState(null);
  const [modules, setModules] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [savingMods, setSavingMods] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageDays, setUsageDays] = useState(30);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/tenants/${id}`);
      setData(data);
      setEnabled(data.tenant.enabled_modules || []);
      setName(data.tenant.name || "");
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    load();
    api.get("/admin/modules").then((r) => setModules(r.data.modules)).catch(() => {});
  }, [id]);
  useEffect(() => {
    setUsage(null);
    api.get(`/admin/tenants/${id}/usage?days=${usageDays}`).then((r) => setUsage(r.data)).catch(() => {});
  }, [id, usageDays]);

  if (!data) return <div className="text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" /> Loading account…</div>;
  const t = data.tenant;

  const saveName = async () => {
    if (!name.trim() || name === t.name) return;
    setSavingName(true);
    try { await api.put(`/admin/tenants/${id}`, { name }); toast.success("Account renamed"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingName(false); }
  };

  const toggle = (k) => setEnabled((e) => (e.includes(k) ? e.filter((x) => x !== k) : [...e, k]));

  const exportCsv = () => {
    if (!usage) return;
    const rows = [["date", "logins", "jobs"], ...usage.series.map((s) => [s.date, s.logins, s.jobs])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${t.name}_usage_${usageDays}d.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };
  const saveModules = async () => {
    setSavingMods(true);
    try { await api.put(`/admin/tenants/${id}/modules`, { enabled_modules: enabled }); toast.success("Modules updated"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingMods(false); }
  };

  const access = async (userId) => {
    try { await enterAccount(id, userId); toast.success(`Now inside ${t.name}`); nav("/dashboard"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setUserStatus = async (u, status) => {
    try { await api.put(`/admin/users/${u.id}`, { status }); toast.success(`User ${status === "Active" ? "enabled" : "disabled"}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email}:`);
    if (!pw) return;
    try { await api.put(`/admin/users/${u.id}`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="text-slate-100">
      <button onClick={() => nav("/platform/accounts")} className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-accounts"><ArrowLeft size={15} /> All accounts</button>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="rename-account-input"
              className="text-2xl font-semibold tracking-tight bg-transparent border-b border-transparent hover:border-white/20 focus:border-indigo-400 outline-none max-w-md" />
            {name !== t.name && (
              <button onClick={saveName} disabled={savingName} data-testid="save-name-btn" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-medium">
                {savingName ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />} Rename
              </button>
            )}
          </div>
          <p className="text-sm text-slate-400 mt-1">{t.status} · {t.user_count} users · {t.client_count} clients · {t.visit_count} visits</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {t.status !== "Deleted" && (
            <button onClick={() => access()} data-testid="detail-access-account-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#009b7f] hover:bg-[#00876e] text-white text-sm font-medium transition-colors">
              <LogIn size={14} /> Access account
            </button>
          )}
          <span className={`text-xs px-2.5 py-1 rounded-full border ${t.status === "Active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : t.status === "Suspended" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"}`}>{t.status}</span>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-semibold flex items-center gap-2"><TrendingUp size={17} className="text-indigo-400" /> Usage — last {usageDays} days</h2>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-white/10 overflow-hidden text-xs" data-testid="usage-range">
              {[7, 30, 90].map((d) => (
                <button key={d} onClick={() => setUsageDays(d)} data-testid={`usage-range-${d}`}
                  className={`px-2.5 py-1 transition-colors ${usageDays === d ? "bg-indigo-500 text-white" : "text-slate-400 hover:bg-white/5"}`}>{d}d</button>
              ))}
            </div>
            <button onClick={exportCsv} disabled={!usage} data-testid="usage-export-btn"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"><Download size={13} /> CSV</button>
          </div>
        </div>
        {usage && <div className="text-xs text-slate-400 mb-3">{usage.active_users}/{usage.total_users} users active · {usage.total_logins} logins · {usage.period_jobs} jobs</div>}
        {usage ? (
          <div className="h-56" data-testid="usage-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <AreaChart data={usage.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gLogins" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#818cf8" stopOpacity={0.5} /><stop offset="95%" stopColor="#818cf8" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gJobs" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#34d399" stopOpacity={0.5} /><stop offset="95%" stopColor="#34d399" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={Math.max(0, Math.floor((usage.series.length || 30) / 7))} />
                <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#e2e8f0" }} />
                <Area type="monotone" dataKey="jobs" name="Jobs" stroke="#34d399" fill="url(#gJobs)" strokeWidth={2} />
                <Area type="monotone" dataKey="logins" name="Logins" stroke="#818cf8" fill="url(#gLogins)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : <div className="h-40 flex items-center justify-center text-slate-500 text-sm"><Loader2 className="animate-spin mr-2" size={16} /> Loading usage…</div>}
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2"><Blocks size={17} className="text-indigo-400" /> Modules</h2>
          <button onClick={saveModules} disabled={savingMods} data-testid="save-modules-btn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium">
            {savingMods ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save modules
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {modules.map((m) => (
            <label key={m.key} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-white/5">
              <input type="checkbox" checked={enabled.includes(m.key)} onChange={() => toggle(m.key)} data-testid={`mod-${m.key}`} className="accent-indigo-500 h-4 w-4 rounded" />
              {m.name}
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2"><Users size={17} className="text-indigo-400" /> Users</h2>
          <button onClick={() => setShowUser(true)} data-testid="add-user-btn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium"><UserPlus size={14} /> Add user</button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left py-2 font-medium">Name</th><th className="text-left py-2 font-medium">Email</th><th className="text-left py-2 font-medium">Role</th><th className="text-left py-2 font-medium">Status</th><th className="text-right py-2 font-medium">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="py-2.5 text-white">{u.name} {u.is_demo && <span className="text-[10px] bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded ml-1">DEMO</span>}</td>
                <td className="py-2.5 text-slate-300">{u.email}</td>
                <td className="py-2.5 text-slate-400">{u.role_name}</td>
                <td className="py-2.5"><span className={u.status === "Active" ? "text-emerald-300" : "text-red-300"}>{u.status}</span></td>
                <td className="py-2.5 text-right space-x-2">
                  {u.status === "Active" && t.status !== "Deleted" && <button onClick={() => access(u.id)} data-testid={`view-as-${u.id}`} className="text-xs text-[#00c49f] hover:text-white">View as</button>}
                  <button onClick={() => resetPw(u)} data-testid={`reset-pw-${u.id}`} className="text-xs text-slate-300 hover:text-white">Reset password</button>
                  {u.status === "Active"
                    ? <button onClick={() => setUserStatus(u, "Disabled")} data-testid={`disable-user-${u.id}`} className="text-xs text-red-400 hover:text-red-300">Disable</button>
                    : <button onClick={() => setUserStatus(u, "Active")} data-testid={`enable-user-${u.id}`} className="text-xs text-emerald-400 hover:text-emerald-300">Enable</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUser && <AddUserModal tid={id} roles={data.roles} onClose={() => setShowUser(false)} onAdded={() => { setShowUser(false); load(); }} />}
    </div>
  );
}

function AddUserModal({ tid, roles, onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role_id: roles[0]?.id || "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name || !form.email || !form.role_id) return toast.error("Name, email and role are required");
    setSaving(true);
    try { await api.post(`/admin/tenants/${tid}/users`, form); toast.success("User added"); onAdded(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/10 p-6" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
        <h2 className="text-lg font-semibold text-white mb-4">Add user</h2>
        <div className="space-y-3">
          <Inp label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="user-name" />
          <Inp label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="user-email" />
          <Inp label="Temporary password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="user-password" placeholder="changeme123" />
          <div>
            <div className="text-xs text-slate-400 mb-1">Role</div>
            <select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="user-role"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-white outline-none focus:border-indigo-400">
              {roles.map((r) => <option key={r.id} value={r.id} className="bg-slate-900">{r.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="save-user-btn" className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium flex items-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Add user
          </button>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <input value={value} data-testid={testid} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-white outline-none focus:border-indigo-400" />
    </div>
  );
}
